import { useCallback, useMemo } from "react";
import {
  MY_PROJECTS_URL,
  NOT_PERMITTED_ORIGIN_PARAMS,
  SIGN_IN_URL,
  SIGN_UP_URL,
} from "../../../consts";
import { useBiLogger } from "../../../bi/useBiLogger";
import { useDropdown } from "../../../hooks/useDropdown";

interface UseHeaderResult {
  isMenuDropdownOpen: boolean;
  setMenuDropdownOpen: (isOpen: boolean) => void;
  shouldShowBackButton: boolean;
  handleBackButtonClick: () => void;
  handleProfileDropdownToggle: () => void;
  handleMySitesClick: () => void;
  handleSignInClick: () => void;
  handleSignUpClick: () => void;
  menuDropdownRef: React.RefObject<HTMLDivElement | null>;
  profileAvatarTriggerRef: React.RefObject<HTMLDivElement | null>;
  isHelpDropdownOpen: boolean;
  setIsHelpDropdownOpen: (isOpen: boolean) => void;
  handleHelpDropdownToggle: () => void;
  helpDropdownRef: React.RefObject<HTMLDivElement | null>;
  helpDropdownTriggerRef: React.RefObject<HTMLDivElement | null>;
}

export const useHeader: () => UseHeaderResult = () => {
  const biLogger = useBiLogger();
  const {
    isOpen: isMenuDropdownOpen,
    setIsOpen: setMenuDropdownOpen,
    toggle: handleProfileDropdownToggle,
    dropdownRef: menuDropdownRef,
    triggerRef: profileAvatarTriggerRef,
  } = useDropdown();

  const {
    isOpen: isHelpDropdownOpen,
    setIsOpen: setIsHelpDropdownOpen,
    toggle: handleHelpDropdownToggle,
    dropdownRef: helpDropdownRef,
    triggerRef: helpDropdownTriggerRef,
  } = useDropdown();

  const shouldShowBackButton = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const hasOriginParam = searchParams.has("origin");
    const hasNotPermittedParams = NOT_PERMITTED_ORIGIN_PARAMS.some((param) =>
      window.location.search.includes(param),
    );
    const hasHistory = window.history.length > 1;
    return (
      hasOriginParam &&
      !hasNotPermittedParams &&
      hasHistory
    );
  }, []);

  const handleMySitesClick = useCallback(() => {
    biLogger.trackUserClickMarketing({ ctaName: "My Sites" });
    window.location.href = MY_PROJECTS_URL;
  }, [biLogger]);

  const handleSignInClick = useCallback(() => {
    biLogger.trackUserClickMarketing({ ctaName: "Sign In" });
    window.location.href = `${SIGN_IN_URL}?postLogin=${window.location.href}&postSignUp=${window.location.href}`;
  }, [biLogger]);

  const handleSignUpClick = useCallback(() => {
    biLogger.trackUserClickMarketing({ ctaName: "Sign Up" });
    window.location.href = `${SIGN_UP_URL}?postLogin=${window.location.href}&postSignUp=${window.location.href}&forceRender=true`;
  }, [biLogger]);

  const handleBackButtonClick = useCallback(() => {
    window.history.back();
  }, []);

  return {
    isMenuDropdownOpen,
    setMenuDropdownOpen,
    shouldShowBackButton,
    handleBackButtonClick,
    handleProfileDropdownToggle,
    handleMySitesClick,
    handleSignInClick,
    handleSignUpClick,
    menuDropdownRef,
    profileAvatarTriggerRef,
    isHelpDropdownOpen,
    setIsHelpDropdownOpen,
    handleHelpDropdownToggle,
    helpDropdownRef,
    helpDropdownTriggerRef,
  };
};
